import { useEffect, useState } from 'react';

/**
 * Emoji shortcodes, the `:heart:` form people type in chat apps.
 *
 * Content editors enter a unit's "symbol" as free text in the admin, so it
 * arrives here either as a real emoji character ("❤️"), a shortcode
 * (":heart:"), or plain words. Shortcodes are resolved against the same
 * emoji-mart dataset the rich-text editor uses; anything else is passed
 * through untouched.
 */

const SHORTCODE_RE = /^:([a-z0-9_+-]+):$/i;

/** `":heart:"` -> `"heart"`; anything that is not a shortcode -> `null`. */
export function parseEmojiShortcode(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const match = SHORTCODE_RE.exec(value.trim());
  return match ? match[1].toLowerCase() : null;
}

/** The slice of the emoji-mart dataset this helper reads. */
export type EmojiShortcodeData = {
  emojis: Record<string, { skins: { native: string }[] }>;
  aliases?: Record<string, string>;
};

/**
 * Pure lookup: shortcode id -> native emoji, following emoji-mart aliases
 * (e.g. `:satisfied:` -> `laughing`). `null` when the id is unknown.
 */
export function resolveEmojiShortcode(id: string, data: EmojiShortcodeData): string | null {
  const canonical = data.emojis[id] ? id : data.aliases?.[id];
  const native = canonical ? data.emojis[canonical]?.skins?.[0]?.native : undefined;
  return native ?? null;
}

let dataPromise: Promise<EmojiShortcodeData> | null = null;

/**
 * The dataset is ~430 KB, so it is loaded on demand the first time a
 * shortcode actually needs resolving, and shared across every caller.
 */
function loadEmojiData(): Promise<EmojiShortcodeData> {
  if (!dataPromise) {
    dataPromise = import('@emoji-mart/data').then((m) => (m.default ?? m) as EmojiShortcodeData);
  }
  return dataPromise;
}

/**
 * Returns `value` with a `:shortcode:` swapped for its emoji.
 *
 * - Not a shortcode → the value comes back unchanged, synchronously.
 * - A shortcode → `null` while the emoji data loads, then the emoji.
 * - An unknown shortcode → the original text, so the mistake stays visible.
 */
export function useEmojiShortcode(value: string | null | undefined): string | null | undefined {
  const id = parseEmojiShortcode(value);
  const [resolved, setResolved] = useState<{ id: string; emoji: string | null } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    loadEmojiData()
      .then((data) => {
        if (!cancelled) setResolved({ id, emoji: resolveEmojiShortcode(id, data) });
      })
      .catch(() => {
        if (!cancelled) setResolved({ id, emoji: null });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id) return value;
  if (!resolved || resolved.id !== id) return null;
  return resolved.emoji ?? value;
}
